import type {
  DataDescriptorFormRow,
  DataDescriptorPayload,
  DropdownItem,
} from './data-descriptor.models';

export const DATA_DESCRIPTOR_STORAGE_KEY = 'dataDescriptors';

const SCHEMA_PREFIX = 'adex:';
const DESCRIPTOR_TYPES = ['adex:DataDescriptor'];

export const PARAMETER_TYPE_OPTIONS: DropdownItem[] = [
  { item_id: 'ValueDescriptor', item_text: 'ValueDescriptor' },
  { item_id: 'Time Series Aggregation', item_text: 'Time Series Aggregation' },
  { item_id: 'Relationship Value', item_text: 'Relationship Value' },
];

export const SCHEMA_TYPE_OPTIONS: DropdownItem[] = [
  { item_id: 'Number', item_text: 'Number' },
  { item_id: 'Text', item_text: 'Text' },
  { item_id: 'Point', item_text: 'Point' },
  { item_id: 'Linestring', item_text: 'Linestring' },
  { item_id: 'Place', item_text: 'Place' },
  { item_id: 'Multipolygon', item_text: 'Multipolygon' },
  { item_id: 'Datetime', item_text: 'Datetime' },
  { item_id: 'Object', item_text: 'Object' },
  { item_id: 'TimeseriesAggregation', item_text: 'TimeseriesAggregation' },
];

function hasParameterType(row: DataDescriptorFormRow): boolean {
  return Array.isArray(row.parameterType) && !!row.parameterType[0]?.item_id;
}

function hasSchemaType(row: DataDescriptorFormRow): boolean {
  return Array.isArray(row.dataSchemaType) && !!row.dataSchemaType[0]?.item_id;
}

export function createEmptyDataDescriptorRow(): DataDescriptorFormRow {
  return {
    attributeName: '',
    parameterType: '',
    dataSchemaType: '',
    attributeObject: {},
    validations: {
      isAttributeNameValid: false,
      isAttributeNameDuplicate: false,
      isParameterTypeValid: false,
      isDataSchemaTypeValid: false,
      isDataSchemaNumberTypeValid: false,
      isDataSchemaNumberUnitCodeTypeValid: false,
      isDataSchemaNumberUnitTextValid: false,
      isDataSchemaOthersDescriptionValid: false,
    },
  };
}

export function initDataDescriptors(): DataDescriptorFormRow[] {
  try {
    const saved = localStorage.getItem(DATA_DESCRIPTOR_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as DataDescriptorFormRow[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // ignore parse errors
  }
  return [createEmptyDataDescriptorRow()];
}

export function addDataDescriptorRow(
  rows: DataDescriptorFormRow[],
): DataDescriptorFormRow[] {
  return [...rows, createEmptyDataDescriptorRow()];
}

export function removeDataDescriptorRow(
  rows: DataDescriptorFormRow[],
  index: number,
): DataDescriptorFormRow[] {
  return rows.filter((_, i) => i !== index);
}

export function selectDropdownValue(value: string): DropdownItem[] {
  return [{ item_id: value, item_text: value }];
}

export function getSchemaTypeId(row: DataDescriptorFormRow): string | undefined {
  return Array.isArray(row.dataSchemaType)
    ? row.dataSchemaType[0]?.item_id
    : undefined;
}

export function isNumberSchema(row: DataDescriptorFormRow): boolean {
  return getSchemaTypeId(row) === 'Number';
}

/** Rows without an attribute name are ignored (e.g. unused "Add data descriptor" slots). */
export function isEmptyDescriptorRow(row: DataDescriptorFormRow): boolean {
  return !row.attributeName?.trim();
}

export function normalizeAttributeNameForComparison(name: string): string {
  return name.trim().toLowerCase();
}

/** Index of another filled row with the same attribute name, or -1 if unique. */
export function findDuplicateAttributeNameIndex(
  rows: DataDescriptorFormRow[],
  attributeName: string,
  excludeIndex?: number,
): number {
  const normalized = normalizeAttributeNameForComparison(attributeName);
  if (!normalized) {
    return -1;
  }

  for (let i = 0; i < rows.length; i++) {
    if (excludeIndex !== undefined && i === excludeIndex) {
      continue;
    }
    if (isEmptyDescriptorRow(rows[i])) {
      continue;
    }
    if (
      normalizeAttributeNameForComparison(rows[i].attributeName) === normalized
    ) {
      return i;
    }
  }
  return -1;
}

export function refreshAttributeNameDuplicateFlags(
  rows: DataDescriptorFormRow[],
): void {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (isEmptyDescriptorRow(row)) {
      row.validations.isAttributeNameDuplicate = false;
      continue;
    }
    row.validations.isAttributeNameDuplicate =
      findDuplicateAttributeNameIndex(rows, row.attributeName, i) >= 0;
  }
}

function hasDuplicateFilledAttributeNames(
  rows: DataDescriptorFormRow[],
): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    if (isEmptyDescriptorRow(row)) {
      continue;
    }
    const key = normalizeAttributeNameForComparison(row.attributeName);
    if (seen.has(key)) {
      return true;
    }
    seen.add(key);
  }
  return false;
}

function clearDescriptorRowValidations(row: DataDescriptorFormRow): void {
  row.validations.isAttributeNameValid = false;
  row.validations.isAttributeNameDuplicate = false;
  row.validations.isParameterTypeValid = false;
  row.validations.isDataSchemaTypeValid = false;
  row.validations.isDataSchemaNumberTypeValid = false;
  row.validations.isDataSchemaNumberUnitCodeTypeValid = false;
  row.validations.isDataSchemaNumberUnitTextValid = false;
  row.validations.isDataSchemaOthersDescriptionValid = false;
}

export function getFilledDescriptorRows(
  rows: DataDescriptorFormRow[],
): DataDescriptorFormRow[] {
  return rows.filter((row) => !isEmptyDescriptorRow(row));
}

export function validateDataDescriptors(rows: DataDescriptorFormRow[]): boolean {
  let isValid = true;
  const filledRows = getFilledDescriptorRows(rows);

  if (filledRows.length === 0) {
    const first = rows[0];
    if (first) {
      first.validations.isAttributeNameValid = true;
    }
    return false;
  }

  for (const row of rows) {
    if (isEmptyDescriptorRow(row)) {
      clearDescriptorRowValidations(row);
      continue;
    }

    row.validations.isAttributeNameValid = !row.attributeName?.trim();
    row.validations.isParameterTypeValid = !hasParameterType(row);
    row.validations.isDataSchemaTypeValid = !hasSchemaType(row);

    const schema = getSchemaTypeId(row);

    if (schema === 'Number') {
      row.validations.isDataSchemaNumberTypeValid =
        !row.attributeObject.description?.trim();
      row.validations.isDataSchemaNumberUnitCodeTypeValid =
        !row.attributeObject.unitCode?.trim();
      row.validations.isDataSchemaNumberUnitTextValid =
        !row.attributeObject.unitText?.trim();
      row.validations.isDataSchemaOthersDescriptionValid = false;
    } else if (schema) {
      row.validations.isDataSchemaOthersDescriptionValid =
        !row.attributeObject.othersDescription?.trim();
      row.validations.isDataSchemaNumberTypeValid = false;
      row.validations.isDataSchemaNumberUnitCodeTypeValid = false;
      row.validations.isDataSchemaNumberUnitTextValid = false;
    } else {
      row.validations.isDataSchemaOthersDescriptionValid = false;
      row.validations.isDataSchemaNumberTypeValid = false;
      row.validations.isDataSchemaNumberUnitCodeTypeValid = false;
      row.validations.isDataSchemaNumberUnitTextValid = false;
    }

    if (
      row.validations.isAttributeNameValid ||
      row.validations.isParameterTypeValid ||
      row.validations.isDataSchemaTypeValid
    ) {
      isValid = false;
      continue;
    }

    if (schema === 'Number') {
      if (
        row.validations.isDataSchemaNumberTypeValid ||
        row.validations.isDataSchemaNumberUnitCodeTypeValid ||
        row.validations.isDataSchemaNumberUnitTextValid
      ) {
        isValid = false;
      }
    } else if (schema && row.validations.isDataSchemaOthersDescriptionValid) {
      isValid = false;
    }
  }

  refreshAttributeNameDuplicateFlags(rows);
  for (const row of rows) {
    if (isEmptyDescriptorRow(row)) {
      continue;
    }
    if (row.validations.isAttributeNameDuplicate) {
      isValid = false;
    }
  }

  return isValid;
}

/** Read-only check that a single descriptor row has all required fields filled. */
export function isDataDescriptorRowComplete(
  row: DataDescriptorFormRow,
  allRows?: DataDescriptorFormRow[],
  rowIndex?: number,
): boolean {
  if (!row.attributeName?.trim()) {
    return false;
  }
  if (!hasParameterType(row)) {
    return false;
  }
  if (!hasSchemaType(row)) {
    return false;
  }

  const schema = getSchemaTypeId(row);
  if (schema === 'Number') {
    if (
      !row.attributeObject.description?.trim() ||
      !row.attributeObject.unitCode?.trim() ||
      !row.attributeObject.unitText?.trim()
    ) {
      return false;
    }
  } else if (schema) {
    if (!row.attributeObject.othersDescription?.trim()) {
      return false;
    }
  }

  if (allRows !== undefined && rowIndex !== undefined) {
    if (findDuplicateAttributeNameIndex(allRows, row.attributeName, rowIndex) >= 0) {
      return false;
    }
  }

  return true;
}

/** Read-only check for enabling Save in the descriptor editor. All rows must be complete. */
export function canSaveDataDescriptors(rows: DataDescriptorFormRow[]): boolean {
  if (rows.length === 0) {
    return false;
  }

  // Every row (including "Not configured" ones) must be fully complete
  for (let i = 0; i < rows.length; i++) {
    if (!isDataDescriptorRowComplete(rows[i], rows, i)) {
      return false;
    }
  }

  return true;
}

export function saveDataDescriptorDraft(rows: DataDescriptorFormRow[]): boolean {
  if (!validateDataDescriptors(rows)) return false;
  const filledRows = getFilledDescriptorRows(rows);
  localStorage.setItem(DATA_DESCRIPTOR_STORAGE_KEY, JSON.stringify(filledRows));
  return true;
}

export function clearDataDescriptorDraft(): void {
  localStorage.removeItem(DATA_DESCRIPTOR_STORAGE_KEY);
}

export function buildDataDescriptorPayload(
  rows: DataDescriptorFormRow[],
): DataDescriptorPayload {
  const result: DataDescriptorPayload = {
    type: [...DESCRIPTOR_TYPES],
  };

  for (const row of rows) {
    const schema = getSchemaTypeId(row);
    const paramType = Array.isArray(row.parameterType)
      ? row.parameterType[0]?.item_id
      : undefined;

    if (!row.attributeName || !schema || !paramType) continue;

    if (schema === 'Number') {
      result[row.attributeName] = {
        type: [paramType],
        dataSchema: `${SCHEMA_PREFIX}${schema}`,
        description: row.attributeObject.description ?? '',
        unitCode: row.attributeObject.unitCode ?? '',
        unitText: row.attributeObject.unitText ?? '',
      };
    } else {
      result[row.attributeName] = {
        type: [paramType],
        dataSchema: `${SCHEMA_PREFIX}${schema}`,
        description: row.attributeObject.othersDescription ?? '',
      };
    }
  }

  return result;
}

/** Map template/JSON array rows into editor form rows (preview step prefill). */
export function importDataDescriptorsFromJson(input: unknown): DataDescriptorFormRow[] {
  if (!Array.isArray(input)) {
    return [];
  }

  const rows: DataDescriptorFormRow[] = [];

  for (const item of input) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const attributeName = String(row['attributeName'] ?? '').trim();
    const parameterTypeRaw = String(row['parameterType'] ?? '').trim();
    const schemaTypeRaw = String(
      row['schemaType'] ?? row['dataSchemaType'] ?? '',
    ).trim();

    if (!attributeName && !parameterTypeRaw && !schemaTypeRaw) continue;

    const schemaId = schemaTypeRaw.replace(/^adex:/i, '');
    const formRow = createEmptyDataDescriptorRow();
    formRow.attributeName = attributeName;
    if (parameterTypeRaw) {
      formRow.parameterType = selectDropdownValue(parameterTypeRaw);
    }
    if (schemaId) {
      formRow.dataSchemaType = selectDropdownValue(schemaId);
    }

    const description = String(row['description'] ?? '').trim();
    const unitCode = String(row['unitCode'] ?? '').trim();
    const unitText = String(row['unitText'] ?? '').trim();

    if (schemaId === 'Number') {
      formRow.attributeObject = {
        description: description || undefined,
        unitCode: unitCode || undefined,
        unitText: unitText || undefined,
      };
    } else if (description) {
      formRow.attributeObject = { othersDescription: description };
    }

    const isDuplicate =
      findDuplicateAttributeNameIndex(rows, attributeName) >= 0;
    if (!isDuplicate) {
      rows.push(formRow);
    }
  }

  return rows;
}

export function deepCopyRows(rows: DataDescriptorFormRow[]): DataDescriptorFormRow[] {
  return rows.map((r) => ({
    ...r,
    parameterType: Array.isArray(r.parameterType) ? [...r.parameterType] : r.parameterType,
    dataSchemaType: Array.isArray(r.dataSchemaType) ? [...r.dataSchemaType] : r.dataSchemaType,
    attributeObject: { ...r.attributeObject },
    validations: { ...r.validations },
  }));
}

export function flattenDataDescriptorForDisplay(
  dataDescriptor: Record<string, unknown>,
): { rows: Array<Array<{ key: string; value: unknown }>>; expanded: boolean[] } {
  const rows: Array<Array<{ key: string; value: unknown }>> = [];

  for (const [attrName, attrValue] of Object.entries(dataDescriptor)) {
    if (attrName === 'type' || attrName === 'dataDescriptorLabel') continue;
    if (typeof attrValue !== 'object' || attrValue === null) continue;

    const entries = Object.entries(attrValue as Record<string, unknown>)
      .filter(([k]) => k !== 'type')
      .map(([key, value]) => ({
        key,
        value:
          typeof value === 'string' && value.includes(':')
            ? value.split(':')[1]
            : value,
      }));

    rows.push([{ key: 'key', value: attrName }, ...entries]);
  }

  return { rows, expanded: rows.map(() => false) };
}
